import React from 'react';
import type { ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import HBBox from '../HBBox/HBBox';
import type { HBBoxProps } from '../HBBox/HBBox.types';
import { withStyles } from '../../styles/layout/withStyles';
import { HBListItemProps } from './HBListItem.types';
import { CONTAINER_PADDING_Y, THEME } from './HBListItem.constants';

// HBBox no longer accepts className (it's not extendable via styled() from
// outside hbui) — withStyles() is the internal-only escape hatch that lets
// this file keep using styled(StyledCapableBox) the way it always has. See
// src/styles/layout/withStyles.tsx.
const StyledCapableBox = withStyles(HBBox);

type BaseStylesProps = Partial<HBListItemProps> & {
    theme: keyof typeof THEME;
};

const focusStyles = `
        outline: 2px solid ${vars.colors.stroke.keyboardFocus};
        outline-offset: 2px;
        z-index: 1;
    `;

// Decorative wrapper — border-radius and the idle/selected background are passed as
// HBBox props (`borderRadius`, `backgroundColor`) at the call site. Hover/selected-hover
// backgrounds stay as CSS below (in ListItemContainer), since they're driven by the
// ancestor's `:hover` pseudo-class, not by props HBBox can react to.
export const ListItemInnerContainer = styled(StyledCapableBox)`
    &:focus-visible,
    &[data-focus],
    &[data-focus-visible] {
        ${focusStyles}
    }
`;

export const ListItemContainer = styled(StyledCapableBox)<BaseStylesProps>`
    padding-top: ${CONTAINER_PADDING_Y}px;
    padding-bottom: ${CONTAINER_PADDING_Y}px;

    color: ${({ theme }) => THEME[theme].cssColor};
    cursor: default;

    &[data-interactive] {
        cursor: pointer;
    }

    // Disabled state using data-disabled attribute
    // Note: no opacity — the disabled color tokens (text.disabled) handle the visual state.
    // Applying opacity would double-dim the already-disabled text color.
    &[data-disabled='true'] {
        cursor: not-allowed;
    }
    &[data-interactive][data-disabled='true'] {
        cursor: not-allowed;
    }

    // data-interactive is set on the container element to allow hover and highlight styles
    // Exclude disabled state from hover effects
    // Non-selected hover
    &[data-interactive]:not([data-disabled='true']):not([data-selected]):hover
        > ${ListItemInnerContainer},
        &[data-interactive]:not([data-disabled='true']):not(
            [data-selected]
        )[data-hover]
        > ${ListItemInnerContainer},
        &[data-interactive]:not([data-disabled='true']):not(
            [data-selected]
        )[data-highlighted]
        > ${ListItemInnerContainer},
        &[data-interactive]:not([data-disabled='true']):not(
            [data-selected]
        )[data-state='open']
        > ${ListItemInnerContainer} {
        background-color: ${({ theme }) => THEME[theme].hoverColor};
    }

    // Selected hover
    &[data-interactive]:not([data-disabled='true'])[data-selected]:hover
        > ${ListItemInnerContainer},
        &[data-interactive]:not(
            [data-disabled='true']
        )[data-selected][data-hover]
        > ${ListItemInnerContainer},
        &[data-interactive]:not(
            [data-disabled='true']
        )[data-selected][data-highlighted]
        > ${ListItemInnerContainer},
        &[data-interactive]:not(
            [data-disabled='true']
        )[data-selected][data-state='open']
        > ${ListItemInnerContainer} {
        background-color: ${({ theme }) => THEME[theme].selectedHoverColor};
    }

    // Propagate focus styling from outer container to inner container
    // (used when data-focus-visible is set on the outer via props, e.g. in stories)
    &[data-focus-visible] > ${ListItemInnerContainer} {
        ${focusStyles}
    }

    // Keyboard navigation inside a navigable list (role="option"):
    // Show hover background and focus ring when an item receives focus via arrow keys.
    // Scoped to [role="option"] so it only applies inside HBList with keyboard nav enabled.
    &[role='option']:focus-visible {
        outline: none;
    }

    &[role='option']:focus-visible > ${ListItemInnerContainer} {
        ${focusStyles}
    }

    &[role='option']:not([data-disabled='true']):not(
            [data-selected]
        ):focus-visible
        > ${ListItemInnerContainer} {
        background-color: ${({ theme }) => THEME[theme].hoverColor};
    }

    &[role='option']:not([data-disabled='true'])[data-selected]:focus-visible
        > ${ListItemInnerContainer} {
        background-color: ${({ theme }) => THEME[theme].selectedHoverColor};
    }
`;

export const ListItemSideElement = styled(StyledCapableBox)`
    color: inherit;

    > i {
        display: flex;
        svg * {
            fill: currentColor;
        }
    }
`;

export const ListItemHoverAction = styled(StyledCapableBox)`
    visibility: hidden;
    ${ListItemContainer}:is(:hover, :focus-visible, :focus-within):not([data-disabled]) & {
        visibility: visible;
    }
`;

const AsButton = (
    props: HBBoxProps & ButtonHTMLAttributes<HTMLButtonElement>
) => <StyledCapableBox as="button" {...props} />;

export const ListItemDragHandle = styled(AsButton)`
    border: none;
    background: none;
    position: relative;
    z-index: 1;
    cursor: grab;
    color: ${vars.colors.text.tertiary};

    &:hover,
    &:focus,
    &:focus-visible,
    &:active {
        background-color: transparent;
        color: ${vars.colors.text.primary};
    }

    &:focus-visible {
        outline: 2px solid ${vars.colors.stroke.keyboardFocus};
    }

    &:active {
        cursor: grabbing;
    }

    ${ListItemContainer}:hover:not([data-disabled='true']) & {
        color: ${vars.colors.text.primary};
    }

    ${ListItemContainer}[data-disabled='true'] & {
        cursor: not-allowed;
        pointer-events: none;
    }

    > i {
        display: flex;
        svg * {
            fill: currentColor;
        }
    }
`;

export const ListItemSelectionIndicator = styled(StyledCapableBox)`
    position: relative;

    > svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;
